/* eslint-disable array-callback-return */
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { Button, Tab, Tabs } from "@mui/material";
import { cloneDeep } from "lodash";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getCustomGradesFiltersThunk } from "store/slices/customGradesSlice";
import { getPeerGroupsFiltersThunk } from "store/slices/peerGroupsSlice";
import Loader from "../../../components/loader";
import CustomAccordion from "../../../components/PayAnalytics/CommonComponents/CustomAccordion";
import CustomButtonComponent from "../../../components/PayAnalytics/CommonComponents/CustomButtonComponent";
import ReportExportDialog from "../../../components/PayAnalytics/ReportExportDialog";
import ReportFilters from "../../../components/PayAnalytics/ReportFilters";
import { ReportOverview } from "../../../components/PayAnalytics/ReportOverview";
import {
  ControlType,
  DataTypes,
  FilterType,
  PayAnalyticsConstants,
  ReportTypes
} from "../../../constants/app.constant";
import {
  DefaultFilterValues,
  FilterCategoryName,
  FilterNames,
  ParticipatePackageAllMarketPayloadKeys,
  ParticipatePackageLockedFilters
} from "../../../constants/Filters";
import { PAGE_URL } from "../../../constants/urls.constants";
import { getLanguageCode } from "../../../utilities/helper";
import ConfigFiltersSchema from "../../../schema/ConfigFiltersSchema.json";
import ExportFiltersSchema from "../../../schema/ExportFiltersSchema.json";
import OrganizationFiltersSchema from "../../../schema/OrganizationFiltersSchema.json";
import { RootState } from "../../../store/reducers";
import {
  configThunk,
  resetConfigState
} from "../../../store/slices/configSlice";
import { CountryListThunk } from "../../../store/slices/countryDetailsSlice";

import { PGCGTags } from "constants/common.constants";
import { pgcgposition } from "model/constants/reward-benchmark";
import { getPayloadLanguage } from "utilities/helper";
import {
  getFiltersThunk,
  getReportOverviewThunk,
  initPayloadMappings,
  resetFilterDataOptions,
  resetFilterErrorData,
  resetFilterPayloads,
  resetState,
  setActiveFilter,
  setFilterPayloads,
  setSelectedFilterToBePersisted,
  setTabIndex,
  updateConfigFilter
} from "../../../store/slices/filtersSlice";
import { defaultLanguageCode } from "../../../store/slices/userSlice";
import styles from "./RewardBenchMark.module.scss";
import "./RewardBenchMark.scss";
import { isParticipatePackage } from "services/ReportsService";

const buildParticipatePackageMarketSelections = (marketFilters: any[] = []) => {
  const selections: any = {};
  for (const filter of marketFilters) {
    if (!ParticipatePackageLockedFilters.includes(filter.name)) continue;
    if (!filter?.options?.length) continue;

    if (filter.controlType === ControlType.GROUP_MULTI_SELECT) {
      const selectGroupOptions: any = {};
      const selectSubOptions: any = { All: true };
      for (const option of filter.options) {
        if (option?.group?.key) {
          selectGroupOptions[option.group.key] = true;
        }
        selectSubOptions[option.key] = true;
      }
      if (filter.groupName) {
        selections[filter.groupName] = selectGroupOptions;
      }
      selections[filter.name] = selectSubOptions;
    } else {
      const selectFilterOptions: any = {};
      for (const option of filter.options) {
        selectFilterOptions[option.key] = true;
      }
      selections[filter.name] = selectFilterOptions;
    }
  }
  return selections;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface IconfigPayload {
  countryCode: string | null;
  language: string;
  year: number;
}
interface IcountryDetails {
  name: string;
  currency: string;
}
const TabPanelContent = ({ children, value, index }: TabPanelProps) => {
  return (
    <div className={styles.tabContent} hidden={value !== index}>
      {children}
    </div>
  );
};

const RewardBenchMark = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(0);
  const [isClientDataAvailable, setIsClientDataAvailable] = useState(false);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<any>({});
  const [appliedFiltersCount, setAppliedFiltersCount] = useState<any>({
    config: 0,
    market: 0,
    organization: 0,
    export: 0
  });
  const [isFilterOptClicked, setFilterOptClicked] = useState(false);
  const [isFilterChanged, setFilterChanged] = useState(false);
  const [isDataEmpty, setIsDataEmpty] = useState(false);
  const [exportFiltersState, setExportFiltersState] = useState<any>([]);
  const dispatch = useDispatch();

  const reportOverviewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  const cancelScheduledReportOverview = () => {
    if (reportOverviewTimerRef.current) {
      clearTimeout(reportOverviewTimerRef.current);
      reportOverviewTimerRef.current = null;
    }
  };

  // Immediate report overview fetch (used for Next / Back navigation)
  const fetchReportOverviewNow = (data: any) => {
    cancelScheduledReportOverview();
    dispatch(
      getReportOverviewThunk({
        data,
        reportName: ReportTypes?.REWARD_BENCHMARK
      })
    );
  };

  // Delayed report overview fetch (used on filter change - only after 5 seconds)
  const scheduleReportOverview = (data: any) => {
    cancelScheduledReportOverview();
    reportOverviewTimerRef.current = setTimeout(() => {
      dispatch(
        getReportOverviewThunk({
          data,
          reportName: ReportTypes?.REWARD_BENCHMARK
        })
      );
    }, 0);
  };

  useEffect(() => {
    return () => cancelScheduledReportOverview();
  }, []);

  const history = useNavigate();
  const queryParameters = new URLSearchParams(window.location.search);
  const [countryCode] = useState(queryParameters.get("countryCode"));
  const [configFiltersList, setConfigFiltersList] = useState<any>([]);
  const [orgFiltersData, setOrgFiltersData] = useState<any>([]);
  const [allYearsConfigListState, setAllYearsConfigListState] = useState<any>(
    []
  );
  const [selectedPeerGroup, setSelectedPeerGroup] = useState("");
  const [selectedCustomGrades, setSelectedCustomGrades] = useState("");

  const { user, country } = useSelector((state: any) => state);
  const { configList, allYearsConfigList } = useSelector(
    (state: RootState) => state.config
  );
  const activetabindex = useSelector(
    (state: any) => state?.filters.activetabindex
  );
  const activeFIlterName = useSelector(
    (state: any) => state?.filters.activeFIlterName
  );
  const { filters, isFiltersLoading, payloadMappings, filterPayloads } =
    useSelector((state: RootState) => state.filters);
  const { peerGroups, isPeerGroupsLoading, peerGroupsError } = useSelector(
    (state: RootState) => state.peerGroups
  );

  const { customGrades, isCustomGradesLoading, customGradesError } =
    useSelector((state: RootState) => state.customGrades);
  const selectedFilterToBePersisted = useSelector(
    (state: any) => state?.filters.selectedFilterToBePersisted
  );
  const { reportOverview } = useSelector((state: any) => {
    return state?.filters;
  });

  useEffect(() => {
    activetabindex && typeof activetabindex === "number"
      ? setActiveTab(activetabindex)
      : setActiveTab(0);
    activetabindex &&
      typeof activetabindex === "number" &&
      handleTabChange(activetabindex);
    dispatch(resetState());
    dispatch(resetConfigState());
    return () => {
      dispatch(resetConfigState());
      dispatch(resetState());
    };
  }, []);

  let mappedPeerGroups: Array<{ key: string; label: string }> = [];
  let mappedCustomGrades: Array<{ key: string; label: string }> = [];

  const mapPeerGroupsToKeyLabel = (peerGroupsArray: any[]) => {
    if (!peerGroupsArray) return [];

    return peerGroupsArray.map((peerGroup) => ({
      key:
        peerGroup.peergroupid === null || peerGroup.peergroupid === undefined
          ? ""
          : String(peerGroup.peergroupid),
      label: peerGroup.peergroupname || ""
    }));
  };

  if (peerGroups?.data?.data?.peerGroups) {
    mappedPeerGroups = mapPeerGroupsToKeyLabel(
      peerGroups.data.data.peerGroups || []
    );
  }

  const mapCustomGradesToKeyLabel = (customGradesArray: any[]) => {
    if (!customGradesArray) return [];

    return customGradesArray.map((customGrade) => ({
      key:
        customGrade.gradesetid === null || customGrade.gradesetid === undefined
          ? ""
          : String(customGrade.gradesetid),
      label: customGrade.gradesetname || ""
    }));
  };

  if (customGrades?.data?.data?.grades) {
    mappedCustomGrades = mapCustomGradesToKeyLabel(
      customGrades.data.data.grades || []
    );
  } else {
    mappedCustomGrades = [];
  }

  const configFiltersWithPlaceholders = useMemo(() => {
    if (!configFiltersList || !configFiltersList.length)
      return configFiltersList;
    const arr = [...configFiltersList];
    const marketYearIndex = arr.findIndex(
      (filter: any) => filter.name === FilterNames.marketYear
    );
    // const peerInsertIndex = pgcgposition.PEER_GROUPS_INDEX;
    // const customGradesInsertIndex = pgcgposition.CUSTOM_GRADES_INDEX;
const currentCountryDetails = country?.countryListAPIData?.countryList?.find((eachCountry: any) => eachCountry.countryCode === country?.selectedCountry?.key);
if(currentCountryDetails && !isParticipatePackage(currentCountryDetails?.productBrandId)){
    const peerInsertIndex =
      marketYearIndex > -1 ? marketYearIndex : pgcgposition.PEER_GROUPS_INDEX;

    arr.splice(peerInsertIndex, 0, {
      name: t("pa.peergroupsname"),
      title: t("pa.peergroupstitle"),
      tag: PGCGTags.PEER_GROUPS,
      description: t("pa.peergroupsdescription"),
      controlType: ControlType.SINGLE_SELECT,
      options: mappedPeerGroups
    });

    const customGradesInsertIndex = peerInsertIndex + 1;

    arr.splice(customGradesInsertIndex, 0, {
      name: t("pa.customgradesname"),
      title: t("pa.customgradestitle"),
      tag: PGCGTags.CUSTOM_GRADES,
      description: t("pa.customgradesdescription"),
      controlType: ControlType.SINGLE_SELECT,
      options: mappedCustomGrades
    });
}
    return arr;
  }, [configFiltersList, mappedPeerGroups, mappedCustomGrades]);

  const fetchPeerGroups = async () => {
    const config = { ...configList[0] };
    try {
      const response = await dispatch(
        getPeerGroupsFiltersThunk({
          mktDefinitionKey: config?.marketDefinitionKey,
          countryCode: country?.countryDetails[0]?.countrycode
        })
      );
    } catch (error) {
      console.error("Error fetching peer groups:", error);
    }
  };
  const fetchCustomGrades = async () => {
    try {
      const responseCustomGrades = await dispatch(
        getCustomGradesFiltersThunk({
          countryCode: country?.countryDetails[0]?.countrycode
        })
      );
    } catch (error) {
      console.error("Error fetching custom grades:", error);
    }
  };

  useEffect(() => {
  const currentCountryDetails = country?.countryListAPIData?.countryList?.find((eachCountry: any) => eachCountry?.countryCode === country?.selectedCountry?.key);
  if(configList && configList.length && currentCountryDetails && !isParticipatePackage(currentCountryDetails?.productBrandId)){
      fetchPeerGroups();
      fetchCustomGrades();
    }
  }, [configList]);

  const initConfig = (year?: number) => {
    setIsConfigLoading(true);
    const languageCode = getLanguageCode();
    const updatedYear = year
      ? year
      : (configList[0]?.marketYear ?? new Date().getFullYear());
    const payload: IconfigPayload = {
      countryCode: countryCode,
      language: languageCode || defaultLanguageCode,
      year: updatedYear
    };
    allYearsConfigListState?.filter(
      (ele: any) => ele?.marketYear === updatedYear
    ).length === 0 && dispatch(configThunk(payload));
  };

  const updateSelectedConfigFilterOptions = (configSelectedFilters: any) => {
    if (activeFIlterName && activeFIlterName === FilterNames?.marketYear) {
      setSelectedFilters({
        ...selectedFilters,
        configFilters: {
          ...selectedFilters[FilterCategoryName?.customize],
          ...configSelectedFilters,
          [FilterNames["yourOrganizationData"]]: {}
        },
        marketFilters: {},
        organizationFilters: {}
      });
      dispatch(resetFilterDataOptions({ config: [] }));
      const selectedMarketYearLabel = filters?.config
        .filter((filter: any) => filter.name == FilterNames?.marketYear)?.[0]
        ?.options?.filter(
          (opt: any) =>
            opt.key == configSelectedFilters[FilterNames?.marketYear]
        )[0]?.label;

      const isSelectedYearAvailable = allYearsConfigListState
        .flat()
        ?.filter((ele: any) => ele?.marketYear == selectedMarketYearLabel);
      activeFIlterName == FilterNames?.marketYear &&
      isSelectedYearAvailable?.length > 0
        ? getOrganizationData(isSelectedYearAvailable)
        : initConfig(Number(selectedMarketYearLabel));

      dispatch(resetFilterDataOptions({ market: [], organization: [] }));
      dispatch(resetFilterPayloads());
    } else {
      if (activeFIlterName && activeFIlterName === FilterNames.dataSelection) {
        setIsClientDataAvailable(
          configSelectedFilters[FilterNames.dataSelection] &&
            configSelectedFilters[FilterNames.dataSelection] ===
              PayAnalyticsConstants.ORGVSMARKET
            ? true
            : false
        );
      }
      setSelectedFilters({
        ...selectedFilters,
        configFilters: {
          ...selectedFilters[FilterCategoryName?.customize],
          ...configSelectedFilters
        }
      });
    }
  };

  const updateDefaultConfigFilterOptions = () => {
    const config = { ...configList[0] };
    const isMarketYearExist = filters?.config.filter((filter: any) => {
      if (filter.name === FilterNames?.marketYear) {
        return filter.options.filter(
          (option: any) => option.label == configList[0]?.marketYear
        );
      }
    });

    setSelectedFilters({
      ...selectedFilters,
      configFilters: {
        [FilterNames["rewardBenchmarkApproach"]]:
          DefaultFilterValues?.benchmarkingApproach,
        [FilterNames["marketYear"]]:
          isMarketYearExist?.length > 0
            ? String(config?.marketDefinitionKey)
            : "",
        [FilterNames.dataSelection]:
          selectedFilters &&
          selectedFilters[FilterCategoryName?.customize] &&
          selectedFilters[FilterCategoryName?.customize][
            FilterNames.dataSelection
          ]
            ? selectedFilters[FilterCategoryName?.customize][
                FilterNames.dataSelection
              ]
            : DefaultFilterValues?.ReportType
      }
    });
  };

  const getOrganizationData = (configList: any[]) => {
    if (configList && configList.length) {
      dispatch(resetFilterDataOptions({ config: [] }));
      let dataSelectionFilterSchema = cloneDeep(
        ConfigFiltersSchema.find(
          (schema: any) => schema.name === FilterNames?.dataSelection
        )
      );
      const countryDetails = country?.countryListAPIData?.countryList?.find(
        (eachCountry: any) =>
          eachCountry.countryCode === country?.selectedCountry?.key
      );
      const hideOrgVsMarket = !!(
        countryDetails &&
        isParticipatePackage(countryDetails?.productBrandId)
      );
      // Participate/basic packages: Market only — hide Organization vs. Market
      if (hideOrgVsMarket && dataSelectionFilterSchema?.options) {
        dataSelectionFilterSchema.options =
          dataSelectionFilterSchema.options.filter(
            (opt: any) => opt.key !== PayAnalyticsConstants.ORGVSMARKET
          );
      }
      const dataSelectionConfigData = {
        isSingleSelect: true,
        label: dataSelectionFilterSchema?.title
      };
      let orgDataFilterSchema = ConfigFiltersSchema.find(
        (schema: any) => schema.name === FilterNames?.yourOrganizationData
      );
      const orgConfigData = {
        isSingleSelect: false,
        label: "Organization Data",
        name: FilterNames?.yourOrganizationData,
        options: [] as any[]
      };
      for (const configData of configList) {
        if (configData["clientPaySubmissionKey"]) {
          orgConfigData.options.push({
            key: configData["clientPaySubmissionKey"],
            label: configData["companyOrgName"]
          });
        }
      }
      if (orgConfigData.options && orgConfigData.options.length) {
        orgConfigData.options.unshift({
          label: "All",
          key: "All"
        });
        const currentDataSelection =
          selectedFilters?.[FilterCategoryName?.customize]?.[
            FilterNames.dataSelection
          ];
        if (
          hideOrgVsMarket &&
          currentDataSelection === PayAnalyticsConstants.ORGVSMARKET
        ) {
          setSelectedFilters({
            ...selectedFilters,
            configFilters: {
              ...selectedFilters?.[FilterCategoryName?.customize],
              [FilterNames.dataSelection]: DefaultFilterValues?.ReportType
            }
          });
        }
        setIsClientDataAvailable(
          !hideOrgVsMarket &&
            currentDataSelection === PayAnalyticsConstants.ORGVSMARKET
        );
        dispatch(
          updateConfigFilter([
            {
              ...orgDataFilterSchema,
              ...orgConfigData
            },
            {
              ...dataSelectionFilterSchema,
              ...dataSelectionConfigData
            }
          ])
        );
      } else {
        setSelectedFilters({
          ...selectedFilters,
          configFilters: {
            [FilterNames.dataSelection]: DefaultFilterValues?.ReportType
          }
        });
        if (dataSelectionFilterSchema) {
          dataSelectionFilterSchema.options =
            dataSelectionFilterSchema?.options.filter((value, index) => !index);
        }
        dispatch(
          updateConfigFilter([
            {
              ...dataSelectionFilterSchema,
              ...dataSelectionConfigData
            }
          ])
        );
        setIsClientDataAvailable(false);
      }
      let module = FilterType.CONFIG;
      if (activeTab === 1) {
        module = FilterType.MARKET;
      } 
      // else if (activeTab === 2) {
      //   module = FilterType.ORGANIZATION;
      // } 
      else if (activeTab === 2) {
        module = FilterType.EXPORT;
      }
      initFiltersRequest(module);
    }
  };

  const getFilterPayloadAndCount = (
    _filters: any = {},
    _payloadMappings: any = {}
  ) => {
    const filterPayload: any = {};
    let filtersCount = 0;
    Object.keys(_filters).map((name: any) => {
      let selectedFilter = _filters[name];
      let options = [];
      if (typeof selectedFilter == DataTypes?.OBJECT) {
        name === FilterNames?.targetRange
          ? (options = Object.values(selectedFilter))
          : (options = Object.keys(selectedFilter).filter(
              (option) =>
                selectedFilter[option] && option !== "All" && "Indeterminate"
            ));
      } else {
        options = selectedFilter !== undefined ? [selectedFilter] : [];
      }
      if (_payloadMappings[name]) {
        name === FilterNames?.targetRange
          ? (filterPayload[_payloadMappings[name]] = options ?? "All")
          : (filterPayload[_payloadMappings[name]] = options.join(","));
      }
    });
    for (const key in filterPayload) {
      if (Object.prototype.hasOwnProperty.call(filterPayload, key)) {
        if (
          filterPayload[key] &&
          key !== "marketJobFamilyKey" &&
          key !== "orgJobFamilyKey"
        )
          filtersCount += 1;
      }
    }
    return {
      filterPayload,
      filtersCount
    };
  };
  const getMarketYear = (marketDefKey: string) => {
    if (!configFiltersList || !configFiltersList.length) return null;
    let index = configFiltersList.findIndex(
      (ele: any) => ele.name === FilterNames?.marketYear
    );
    if (index < 0) return null;

    let keyIndex = configFiltersList[index]?.options?.findIndex(
      (ele: any) => ele.key === marketDefKey
    );
    let year =
      keyIndex > -1 ? configFiltersList[index]?.options[keyIndex]?.label : null;
    return year ? Number(year) : null;
  };
  const getMarketDefnitionKey = (year: any) => {
    if (!configFiltersList || !configFiltersList.length) return null;
    let index = configFiltersList.findIndex(
      (ele: any) => ele.name === FilterNames?.marketYear
    );
    if (index < 0) return null;

    let keyIndex = configFiltersList[index]?.options?.findIndex(
      (ele: any) => ele.label == year
    );
    let key =
      keyIndex > -1 ? configFiltersList[index]?.options[keyIndex]?.key : null;
    return key ? Number(key) : null;
  };
  const getRequestPayload = (overrideFilters?: any) => {
    const _selectedFilters = overrideFilters ?? selectedFilters;
    const configFilters = _selectedFilters?.configFilters ?? {};
    const config = getFilterPayloadAndCount(
      configFilters,
      payloadMappings.config
    );
    const marketFilters = _selectedFilters.marketFilters ?? {};
    const market = getFilterPayloadAndCount(
      marketFilters,
      payloadMappings.market
    );
    const orgFilters = _selectedFilters.organizationFilters ?? {};
    const organization = getFilterPayloadAndCount(
      orgFilters,
      payloadMappings.organization
    );
    const exportFilters = _selectedFilters.downloadYourReport ?? {};
    const exportData = getFilterPayloadAndCount(
      exportFilters,
      payloadMappings.export
    );
    const defaultOrgIDs =
      configList && configList.length
        ? configList.map((data: any) => data["clientPaySubmissionKey"]).join()
        : "";
    /* const marketDefinitionKey = getMarketDefnitionKey(
      config?.filterPayload?.marketYear
    ); */
    const marketYear = getMarketYear(config.filterPayload.marketYear);

    let reportConfig = {} as any;
    if (configList && configList.length) {
      reportConfig = { ...configList[0] };
    }
    let payload = {
      ...filterPayloads,
      ...config.filterPayload,
      ...market.filterPayload,
      ...organization.filterPayload,
      ...exportData.filterPayload,
      countryCode: countryCode,
      languageKey: reportConfig.langKey,
      geographyKey: reportConfig.geographyKey,
      clientData: organization?.filterPayload?.orgClientTypeKey,
      marketDefinitionKey: config?.filterPayload?.marketYear
        ? Number(config.filterPayload.marketYear)
        : reportConfig.marketDefinitionKey,
      clientPaySubmissionKey:
        organization.filterPayload &&
        organization.filterPayload?.clientPaySubmissionKey
          ? organization.filterPayload.clientPaySubmissionKey
          : defaultOrgIDs,
      allClientPaySubmissionKey:
        config.filterPayload && config.filterPayload?.clientPaySubmissionKey
          ? config.filterPayload.clientPaySubmissionKey
          : defaultOrgIDs,
      marketCompElementKey:
        exportData?.filterPayload?.marketCompElementKey ??
        reportConfig?.compensationFactKey,
      orgCompElementKey:
        exportData?.filterPayload?.marketCompElementKey ??
        reportConfig?.compensationFactKey,
      marketYear: marketYear ? marketYear : reportConfig.marketYear,
      minimumBroadening: exportData?.filterPayload?.minimumBroadening,
      percentileOrder: exportData?.filterPayload?.percentileOrder,
      raisePay: exportData?.filterPayload?.raisePay,
      raiseMarket: exportData?.filterPayload?.raiseMarket,
      reportOnColumns: exportData?.filterPayload?.reportOnColumns,
      reportType: exportData?.filterPayload?.reportType,
      targetRangeMin:
        typeof _selectedFilters?.downloadYourReport?.TargetRange ==
          DataTypes?.OBJECT && exportData?.filterPayload?.targetRange[0],
      targetRangeMax:
        typeof _selectedFilters?.downloadYourReport?.TargetRange ==
          DataTypes?.OBJECT && exportData?.filterPayload?.targetRange[1],
      SelFilCategoryName: activeFIlterName,
      language: getPayloadLanguage(),
      peerGroupId: selectedPeerGroup.toString(),
      gradeSetId: selectedCustomGrades?.toString()
    };
    const { targetRange, ...updatedpayload } = payload;
    for (const [key, value] of Object.entries(updatedpayload)) {
      if (!updatedpayload[key] && DefaultFilterValues[key]) {
        updatedpayload[key] = DefaultFilterValues[key];
      }
    }

    if (isParticipatePkg) {
      for (const key of ParticipatePackageAllMarketPayloadKeys) {
        updatedpayload[key] = "ALL";
      }
    }

    return {
      config: config,
      market: market,
      organization: organization,
      payload: updatedpayload
    };
  };
  const initFiltersRequest = (module = "", filtersCount = 0) => {
    const { config, market, organization, payload } = getRequestPayload();
    const shouldFetchOverview = filtersCount === 0 || isFilterChanged;
    dispatch(setFilterPayloads(payload));

    setAppliedFiltersCount({
      ...appliedFiltersCount,
      config: config.filtersCount,
      market: market.filtersCount,
      organization: organization.filtersCount
    });
    dispatch(setActiveFilter(""));
    payload.SelFilCategoryName = "";
    if (
      filtersCount === 0 ||
      (isFilterChanged && module !== FilterType.CONFIG)
    ) {
      dispatch(
        getFiltersThunk({
          data: payload,
          module: module,
          reportId: ReportTypes?.REWARD_BENCHMARK
        })
      );
    }

    if (shouldFetchOverview) {
      // Next / Back navigation -> fetch report overview immediately
      fetchReportOverviewNow(payload);
    }

    setFilterChanged(false);
  };

  const downloadReport = async () => {
    const { payload } = getRequestPayload();
    dispatch(setFilterPayloads(payload));
    setShowExportDialog(true);
    dispatch(setSelectedFilterToBePersisted({}));
    dispatch(setTabIndex(0));
  };

  const updateFilterOptions = (filterModule = "", payLoad: any) => {
    if (isFilterOptClicked || isDataEmpty) {
      for (const [key, value] of Object.entries(payLoad)) {
        if (!payLoad[key] && DefaultFilterValues[key]) {
          payLoad[key] = DefaultFilterValues[key];
        }
      }
      const orgFilters = selectedFilters.organizationFilters ?? {};
      const organization = getFilterPayloadAndCount(
        orgFilters,
        payloadMappings.organization
      );
      const defaultOrgIDs =
        configList && configList.length
          ? configList.map((data: any) => data["clientPaySubmissionKey"]).join()
          : "";
      let updatedPayload = {
        ...payLoad,
        language: getPayloadLanguage(),
        SelFilCategoryName: activeFIlterName,
        clientPaySubmissionKey:
          organization.filterPayload?.clientPaySubmissionKey &&
          organization.filterPayload?.clientPaySubmissionKey != ""
            ? organization.filterPayload?.clientPaySubmissionKey
            : defaultOrgIDs
      };
      if (isParticipatePkg) {
        for (const key of ParticipatePackageAllMarketPayloadKeys) {
          updatedPayload[key] = "ALL";
        }
      }
      dispatch(setFilterPayloads(updatedPayload));
      if (activeFIlterName) {
        dispatch(
          getFiltersThunk({
            data: updatedPayload,
            module: filterModule,
            reportId: ReportTypes?.REWARD_BENCHMARK
          })
        );
        // Filter change -> fetch report overview only after 5 seconds
        scheduleReportOverview(updatedPayload);
      }
      setFilterOptClicked(false);
    }
  };

  const updateTabFilterCount = () => {
    if (activeTab === 0) {
      const persistfilters = {
        filterType: FilterType.CONFIG,
        selectedFilters: selectedFilters?.configFilters ?? {}
      };
      dispatch(setSelectedFilterToBePersisted(persistfilters ?? {}));
      const configFilters = selectedFilters?.configFilters ?? {};
      const config = getFilterPayloadAndCount(
        configFilters,
        payloadMappings.config
      );
      setAppliedFiltersCount({
        ...appliedFiltersCount,
        config: config.filtersCount
      });
      if (activeFIlterName === FilterNames.rewardBenchmarkApproach) {
        const marketFilters = selectedFilters.marketFilters ?? {};
        const market = getFilterPayloadAndCount(
          marketFilters,
          payloadMappings.market
        );
        updateFilterOptions(FilterType.MARKET, {
          ...filterPayloads,
          ...config.filterPayload,
          ...market.filterPayload
        });
      }
    } else if (activeTab === 1) {
      const persistfilters = {
        filterType: FilterType.MARKET,
        selectedFilters: selectedFilters?.marketFilters ?? {}
      };
      dispatch(setSelectedFilterToBePersisted(persistfilters ?? {}));
      const marketFilters = selectedFilters.marketFilters ?? {};
      const market = getFilterPayloadAndCount(
        marketFilters,
        payloadMappings.market
      );
      setAppliedFiltersCount({
        ...appliedFiltersCount,
        market: market.filtersCount
      });
      updateFilterOptions(FilterType.MARKET, {
        ...filterPayloads,
        ...market.filterPayload
      });
    } 
    // else if (activeTab === 2) {
    //   const persistfilters = {
    //     filterType: FilterType.ORGANIZATION,
    //     selectedFilters: selectedFilters?.organizationFilters ?? {}
    //   };
    //   dispatch(setSelectedFilterToBePersisted(persistfilters ?? {}));
    //   const orgFilters = selectedFilters.organizationFilters ?? {};
    //   const organization = getFilterPayloadAndCount(
    //     orgFilters,
    //     payloadMappings.organization
    //   );
    //   setAppliedFiltersCount({
    //     ...appliedFiltersCount,
    //     organization: organization.filtersCount
    //   });
    //   updateFilterOptions(FilterType.ORGANIZATION, {
    //     ...filterPayloads,
    //     ...organization.filterPayload
    //   });
    // } 
    else if (activeTab === 2) {
      const persistfilters = {
        filterType: FilterType.EXPORT,
        selectedFilters: selectedFilters?.downloadYourReport ?? {}
      };
      dispatch(setSelectedFilterToBePersisted(persistfilters ?? {}));
      const exportFilters = selectedFilters.downloadYourReport ?? {};
      const exportData = getFilterPayloadAndCount(
        exportFilters,
        payloadMappings.export
      );
      setAppliedFiltersCount({
        ...appliedFiltersCount,
        export: exportData.filtersCount
      });
    }
  };
  useEffect(() => {
    if (configList && configList.length) {
      dispatch(
        resetFilterErrorData({
          config: {
            isEmpty: false,
            isFailed: false
          }
        })
      );
      const config = { ...configList[0] };
      dispatch(
        setFilterPayloads({
          ...filterPayloads,
          languageKey: config.langKey,
          geographyKey: config.geographyKey,
          marketDefinitionKey: config.marketDefinitionKey,
          marketCompElementKey: config.compensationFactKey,
          orgCompElementKey: config.compensationFactKey
        })
      );
      if (filters.config.length === 0) {
        getOrganizationData(configList);
      }
    } else {
      setConfigFiltersList([]);
      dispatch(
        resetFilterErrorData({
          config: {
            isEmpty: false,
            isFailed: true
          }
        })
      );
    }
    setIsConfigLoading(false);
  }, [configList]);

  useEffect(() => {
    setFilterChanged(true);
    updateTabFilterCount();
  }, [selectedFilters, selectedPeerGroup, selectedCustomGrades]);

  const didMountPeerGroupCustomGrade = useRef(false);
  useEffect(() => {
    if (!didMountPeerGroupCustomGrade.current) {
      didMountPeerGroupCustomGrade.current = true;
      return;
    }
    const { payload } = getRequestPayload();
    dispatch(setFilterPayloads(payload));
    dispatch(
      getFiltersThunk({
        data: payload,
        module: FilterType.MARKET,
        reportId: ReportTypes?.REWARD_BENCHMARK
      })
    );
    // Filter change (peer group / custom grade) -> fetch report overview only after 5 seconds
    scheduleReportOverview(payload);
  }, [selectedPeerGroup, selectedCustomGrades]);

  useEffect(() => {
    if (country?.countryList?.length < 1) {
      dispatch(CountryListThunk(""));
    }
    initConfig();

    dispatch(initPayloadMappings());
  }, []);

  const handleTabChange = (tabIndex: number, action: string = "") => {
    if (activeTab === 0) {
      dispatch(setActiveFilter(""));
    }
    setActiveTab(tabIndex);
    dispatch(setTabIndex(tabIndex));

    if (tabIndex === 0) {
      initFiltersRequest(FilterType.CONFIG, filters.config.length);
    }
    if (tabIndex === 1) {
      initFiltersRequest(FilterType.MARKET, filters.market.length);
    }
    // if (tabIndex === 2) {
    //   if (isClientDataAvailable) {
    //     initFiltersRequest(
    //       FilterType.ORGANIZATION,
    //       filters.organization.length
    //     );
    //   } else {
    //     if (action && action === "Next") {
    //       handleTabChange(tabIndex + 1);
    //     } else if (action && action === "Back") {
    //       handleTabChange(tabIndex - 1);
    //     }
    //   }
    // }
    if (tabIndex === 2) {
      initFiltersRequest(FilterType.EXPORT, filters.export.length);
    }
  };

  const renderTabHeader = (label: string, type: string = "") => {
    return (
      <div className="tab-header-label">
        {label}
        <span className="tab-filter-count">
          {label ===
            t("pa.createYourRewardBenchmarkingReport.OrganizationFilters") &&
          !isClientDataAvailable
            ? 0
            : appliedFiltersCount[type]}
        </span>
      </div>
    );
  };

  const isLoading = () => {
    if (isConfigLoading || isFiltersLoading) {
      return true;
    }
  };

  useEffect(() => {
    if (filters?.config.length) {
      setConfigFiltersList(filters?.config);
      updateDefaultConfigFilterOptions();
    } else {
      setConfigFiltersList([]);
    }
  }, [filters?.config]);

  const currentCountryDetails = useMemo(() => {
    return country?.countryListAPIData?.countryList?.find(
      (eachCountry: any) =>
        eachCountry.countryCode === country?.selectedCountry?.key
    );
  }, [country?.countryListAPIData, country?.selectedCountry?.key]);

  const isParticipatePkg = useMemo(() => {
    return !!(
      currentCountryDetails &&
      isParticipatePackage(currentCountryDetails?.productBrandId)
    );
  }, [currentCountryDetails]);

  const marketFiltersForUi = useMemo(() => {
    if (!filters?.market?.length) return [];
    if (!isParticipatePkg) return filters.market;
    return filters.market.map((filter: any) => ({
      ...filter,
      disabled: ParticipatePackageLockedFilters.includes(filter.name)
    }));
  }, [filters?.market, isParticipatePkg]);

  useEffect(() => {
    if (!isParticipatePkg || !filters?.market?.length) return;
    const allSelected = buildParticipatePackageMarketSelections(filters.market);
    if (!Object.keys(allSelected).length) return;
    setSelectedFilters((prev: any) => ({
      ...prev,
      marketFilters: {
        ...(prev.marketFilters || {}),
        ...allSelected
      }
    }));
  }, [filters?.market, isParticipatePkg]);

  useEffect(() => {
    if (filters?.organization.length > 0 && isClientDataAvailable) {
      const orgFilters = selectedFilters[FilterCategoryName?.organization]
        ? Object.keys(selectedFilters[FilterCategoryName?.organization]).length
          ? selectedFilters[FilterCategoryName?.organization]
          : { ClientDataType: DefaultFilterValues?.orgClientTypeKey }
        : { ClientDataType: DefaultFilterValues?.orgClientTypeKey };
      setSelectedFilters({
        ...selectedFilters,
        organizationFilters: orgFilters
      });
    }
  }, [filters?.organization.length]);

  useEffect(() => {
    const getOrgFiltersJson: any = () => {
      let filtersSchema = [...filters?.organization];
      const config = { ...configList[0] };
      if (filtersSchema) {
        const getFilterOptions = (filterName: string) => {
          if (
            filterName === FilterNames?.businessUnit &&
            config?.clientPaySubmissionKey > 0
          ) {
            let filterOptions = [];
            for (const configData of configList) {
              if (configData["clientPaySubmissionKey"]) {
                filterOptions.push({
                  key: configData["clientPaySubmissionKey"],
                  label: configData["companyOrgName"]
                });
              }
            }
            filterOptions.unshift({
              label: "All",
              key: "All"
            });
            return filterOptions;
          }
          let filter = [];
          filter = filtersSchema.filter((schema: any) => {
            return schema.name == filterName;
          });
          return filter[0]?.options;
        };
        OrganizationFiltersSchema.map((schema: any) => {
          if (
            schema.name === FilterNames?.businessUnit &&
            config?.clientPaySubmissionKey > 0
          ) {
            filtersSchema.unshift({
              ...schema,
              isSingleSelect:
                schema?.controlType === ControlType?.SINGLE_SELECT
                  ? true
                  : false,
              label: schema?.title,
              options: getFilterOptions(schema.name) ?? schema?.options
            });
          } else if (schema.name === FilterNames?.clientDataType) {
            filtersSchema.unshift({
              ...schema,
              isSingleSelect:
                schema?.controlType === ControlType?.SINGLE_SELECT
                  ? true
                  : false,
              label: schema?.title,
              options: getFilterOptions(schema.name) ?? schema?.options
            });
          }
        });
        setOrgFiltersData(filtersSchema);
      }
    };
    getOrgFiltersJson();
  }, [filters?.organization]);

  useEffect(() => {
    const getExportYourReportJson: any = () => {
      let filtersSchema = [...filters?.export];
      if (filtersSchema) {
        const getFilterOptions = (filterName: string) => {
          let filter = [];
          filter = filtersSchema.filter((schema: any) => {
            return schema.name == filterName;
          });
          return filter[0]?.options;
        };
        let updatedFiltersSchema = ExportFiltersSchema.filter((item) => {
          return !item.isHideFilter;
        });
        updatedFiltersSchema.map((schema: any) => {
          if (schema.name === FilterNames?.compensation) {
            filtersSchema.push({
              ...schema,
              isSingleSelect:
                schema?.controlType === ControlType?.SINGLE_SELECT
                  ? true
                  : false,
              label: schema?.title,
              options:
                getFilterOptions(FilterNames?.compensation) ?? schema?.options
            });
          } else {
            if (
              country?.countryDetails[0]?.countryCode === "AU" &&
              schema.name ===
                FilterNames?.minimumNumberOfOrganizationsBeforeBroadening
            ) {
              filtersSchema.push({
                ...schema,
                isSingleSelect:
                  schema?.controlType === ControlType?.SINGLE_SELECT
                    ? true
                    : false,
                label: schema?.title,
                controls: {
                  ...schema?.controls,
                  min: 5
                }
              });
            }
            filtersSchema.push({
              ...schema,
              isSingleSelect:
                schema?.controlType === ControlType?.SINGLE_SELECT
                  ? true
                  : false,
              label: schema?.title
            });
          }
        });
        filtersSchema.splice(0, 1);
        const filtersToBeHide = [
          "raisePay",
          "raiseMarket",
          "targetRange",
          "reportType",
          "minimumBroadening",
          "percentileOrder"
        ];

        let Updatedfilters = [];

        for (const filter of filtersSchema) {
          if (!filtersToBeHide.includes(filter.payloadKey)) {
            Updatedfilters.push(filter);
          }
        }
        setExportFiltersState(Updatedfilters);
      }
    };
    getExportYourReportJson();

    if (filters?.export?.length > 0) {
      setSelectedFilters({
        ...selectedFilters,
        downloadYourReport: {
          COMPENSATION: selectedFilters?.downloadYourReport?.COMPENSATION ?? {
            All: "Indeterminate",
            [configList[0]?.compensationFactKey]: true
          },
          ReportLayout:
            selectedFilters?.downloadYourReport?.ReportLayout ??
            DefaultFilterValues?.ReportOnColumns
        }
      });
    } else {
      setSelectedFilters({
        ...selectedFilters,
        downloadYourReport: {
          ReportLayout:
            selectedFilters?.downloadYourReport?.ReportLayout ??
            DefaultFilterValues?.ReportOnColumns
        }
      });
    }
  }, [filters?.export]);
  // useEffect(() => {
  //   setSelectedFilters({
  //     ...selectedFilters,
  //     downloadYourReport: {
  //       PERCENTILE: "0.5",
  //       COMPENSATION: { [configList[0]?.compensationFactKey]: true },
  //       PERCENTILEORDER: "Ascending",
  //       RaiseLowerPay: 0,
  //       RaiseLowerMarket: 0,
  //       TargetRange: { min: 0, max: 100 },
  //       DataSelection: "Market",
  //       MinimumNumberOfOrganizationsBeforeBroadening: 4,
  //       ReportLayout: 0
  //     }
  //   });
  // }, [configList, filters?.export, exportFiltersState]);
  const disableExportFunc = (): any => {
    const isTabsHasFilters = () => {
      if (configFiltersList && configFiltersList.length > 0) {
        return false;
      } else if (exportFiltersState) {
        return false;
      } else if (filters?.market.length > 0) {
        return false;
      } else if (filters?.organization.length > 0) {
        return false;
      } else {
        return true;
      }
    };
    const isReportDataLess = (name: string) => {
      if (name === FilterCategoryName?.downloadYourReport) {
        if (reportOverview?.jobRoleFeedback?.numberOfOrganizationsMarket < 4) {
          return true;
        }
      }
      return false;
    };

    return (
      isTabsHasFilters() ||
      isReportDataLess(FilterCategoryName?.downloadYourReport)
    );
  };

  useEffect(() => {
    if (activeTab) {
      dispatch(setTabIndex(activeTab));
      setActiveTab(activeTab);
    }
    setSelectedFilters({
      ...selectedFilters,
      configFilters: selectedFilterToBePersisted?.config,
      marketFilters: selectedFilterToBePersisted?.market,
      organizationFilters: selectedFilterToBePersisted?.organization,
      downloadYourReport: selectedFilterToBePersisted?.export
    });
  }, []);

  const handleOrgTabSelectedFilters = (_selectedFilters: any) => {
    if (Object.keys(_selectedFilters)[0] === FilterNames?.clientDataType) {
      setSelectedFilters({
        ...selectedFilters,
        organizationFilters: {
          ...selectedFilters[FilterCategoryName?.organization],
          ..._selectedFilters
        }
      });
      dispatch(
        setFilterPayloads({
          ...filterPayloads,
          clientData: _selectedFilters.ClientDataType
        })
      );
    } else {
      setSelectedFilters({
        ...selectedFilters,
        organizationFilters: {
          ...selectedFilters[FilterCategoryName?.organization],
          ..._selectedFilters
        }
      });
    }
    setFilterOptClicked(true);
  };

  const handleExportTabSelectedFilters = (_selectedFilters: any) => {
    const updatedSelectedFilters = {
      ...selectedFilters,
      downloadYourReport: {
        ...selectedFilters[FilterCategoryName?.downloadYourReport],
        ..._selectedFilters
      }
    };
    setSelectedFilters(updatedSelectedFilters);
    setFilterOptClicked(false);

    if (activeFIlterName === FilterNames?.compensation) {
      const { payload } = getRequestPayload(updatedSelectedFilters);
      dispatch(setFilterPayloads(payload));
      dispatch(
        getFiltersThunk({
          data: payload,
          module: FilterType.EXPORT,
          reportId: ReportTypes?.REWARD_BENCHMARK
        })
      );
      // Filter change -> fetch report overview only after 5 seconds
      scheduleReportOverview(payload);
    }
  };

  return (
    <div
      className={`reward-benchmark-main-block ${styles.pageContainer} ${
        isLoading() ? "reportDataLoadingInprogress" : ""
      }`}
    >
      <div className={`${styles.pageBody} container`}>
        <div className={styles.reportHeader}>
          <div className={styles.titleBlock}>
            <div className={styles.pageTitle}>
              <h1 className={styles.title}>
                {t("pa.createYourRewardBenchmarkingReport") + " -"}
              </h1>
              <p className={styles.subtext}>
                <span className={styles.subtextPipeContent}>
                  <strong>
                    {t("pa.createYourRewardBenchmarkingReport.MyMarket")}
                  </strong>{" "}
                  : {country?.selectedCountry?.label}
                </span>
                |
                <span className={styles.subtextPipeContent}>
                  <strong>
                    {t("pa.createYourRewardBenchmarkingReport.Currency")}
                  </strong>
                  : {country?.countryDetails[0]?.currencyName}
                </span>
              </p>
            </div>
            <div className={styles.headerExportButton}>
              <CustomButtonComponent
                variant="outlined"
                onClick={() => {
                  downloadReport();
                }}
                text={t("pa.exportReportBtn")}
                disabled={disableExportFunc()}
              />
            </div>
          </div>
          {/* <div className={styles.LegacyTextAccodion}>
            {" "}
            <CustomAccordion
              title={t(
                "pa.createYourRewardBenchmarkingReport.LegacyTextHeader"
              )}
              description={t(
                "pa.createYourRewardBenchmarkingReport.LegacyTextDescription"
              )}
            />
          </div> */}
        </div>
        <div className={styles.reportBody}>
          <div className={styles.reportBodyContainer}>
            <div className={styles.bodySection}>
              <div className={styles.tabContainer}>
                <Tabs
                  value={activeTab}
                  onChange={(event, tabIndex) => {
                    handleTabChange(tabIndex);
                    dispatch(setTabIndex(tabIndex));
                  }}
                  className="pa-tabs-wrapper"
                  variant="scrollable"
                  scrollButtons="auto"
                  TabIndicatorProps={{
                    children: <span className="MuiTabs-indicatorSpan" />
                  }}
                >
                  <Tab
                    label={renderTabHeader(
                      t("pa.createYourRewardBenchmarkingReport.Customize"),
                      "config"
                    )}
                    id="tab-1"
                  />
                  <Tab
                    label={renderTabHeader(
                      t("pa.createYourRewardBenchmarkingReport.MarketFilters"),
                      "market"
                    )}
                    id="tab-2"
                  />
                  {/* <Tab
                    label={renderTabHeader(
                      t(
                        "pa.createYourRewardBenchmarkingReport.OrganizationFilters"
                      ),
                      "organization"
                    )}
                    id="tab-3"
                    disabled={!isClientDataAvailable}
                  /> */}
                  <Tab
                    label={renderTabHeader(
                      t(
                        "pa.createYourRewardBenchmarkingReport.ExportYourReport"
                      ),
                      "export"
                    )}
                    id="tab-3"
                  />
                </Tabs>
              </div>
              <TabPanelContent index={0} value={activeTab}>
                {configFiltersList && configFiltersList?.length > 0 ? (
                  <ReportFilters
                    name={FilterCategoryName?.customize}
                    title={t("pa.configuration.Heading")}
                    description={t("pa.configuration.Subtext")}
                    filters={configFiltersWithPlaceholders}
                    peerGroups={mappedPeerGroups}
                    selectedPeerGroup={selectedPeerGroup}
                    setSelectedPeerGroup={setSelectedPeerGroup}
                    customGrades={mappedCustomGrades}
                    selectedCustomGrades={selectedCustomGrades}
                    setSelectedCustomGrades={setSelectedCustomGrades}
                    selectedFilterValues={
                      selectedFilters[FilterCategoryName?.customize]
                    }
                    onFilterSelected={(_selectedFilters) => {
                      updateSelectedConfigFilterOptions(_selectedFilters);
                      setFilterOptClicked(true);
                    }}
                  />
                ) : (
                  !isLoading() &&
                  (typeof configFiltersList === DataTypes?.STRING ? (
                    <div className="no-data"> {configFiltersList}</div>
                  ) : (
                    <div className={styles.noOrgData}>
                      {filters?.error?.config?.isEmpty && (
                        <div className={styles.tryAgainText}>
                          {t("pa.noFiltersOptionFound")}
                        </div>
                      )}
                      {filters.error?.config?.isFailed && (
                        <>
                          <div className={styles.tryAgainText}>
                            <b>{t("pa.hmmTryAagin")}</b>
                          </div>
                          <p>{t("pa.tryReloading")}</p>
                        </>
                      )}
                    </div>
                  ))
                )}
              </TabPanelContent>
              <TabPanelContent index={1} value={activeTab}>
                {filters?.market && filters?.market?.length > 0 ? (
                  <ReportFilters
                    name={FilterCategoryName?.market}
                    title={t("pa.marketFilters.Heading")}
                    description={t("pa.marketFilters.Subtext")}
                    filters={marketFiltersForUi}
                    selectedFilterValues={
                      selectedFilters[FilterCategoryName?.market]
                    }
                    onFilterSelected={(_selectedFilters) => {
                      setSelectedFilters((prev: any) => ({
                        ...prev,
                        marketFilters: {
                          ...(prev[FilterCategoryName?.market] || {}),
                          ..._selectedFilters
                        }
                      }));
                      const editableKeys = Object.keys(
                        _selectedFilters || {}
                      ).filter(
                        (key) =>
                          !ParticipatePackageLockedFilters.includes(key) &&
                          key !== FilterNames.jobFamily
                      );
                      if (!isParticipatePkg || editableKeys.length > 0) {
                        setFilterOptClicked(true);
                      }
                    }}
                  />
                ) : (
                  !isLoading() &&
                  (typeof filters?.market === DataTypes?.STRING ? (
                    <div className="no-data"> {filters?.market}</div>
                  ) : (
                    <div className={styles.noOrgData}>
                      {filters.error?.market?.isEmpty && (
                        <>
                          <div className={styles.tryAgainText}>
                            {t("pa.noFiltersOptionFound")}
                          </div>
                          <div>
                            {" "}
                            <Button
                              type="button"
                              onClick={() => {
                                let localfilters = {
                                  ...selectedFilterToBePersisted.market
                                };
                                if (activeFIlterName === "JOBFAMILY") {
                                  localfilters[activeFIlterName] = "";
                                  localfilters["JOBSUBFAMILY"] = "";
                                } else {
                                  localfilters[activeFIlterName] = "";
                                }
                                setIsDataEmpty(true);
                                setSelectedFilters({
                                  ...selectedFilters,
                                  marketFilters: localfilters
                                });
                                handleTabChange(activeTab - 1, "Back");
                              }}
                            >
                              {t("pa.backtoFiltersScreen")}
                            </Button>
                          </div>
                        </>
                      )}
                      {filters.error?.market?.isFailed && (
                        <>
                          <div className={styles.tryAgainText}>
                            <b>{t("pa.hmmTryAagin")}</b>
                          </div>
                          <p>{t("pa.tryReloading")}</p>
                        </>
                      )}
                    </div>
                  ))
                )}
              </TabPanelContent>
              {/* <TabPanelContent index={2} value={activeTab}>
                {filters?.organization && filters?.organization?.length > 0 ? (
                  <ReportFilters
                    name={FilterCategoryName?.organization}
                    title={t("pa.organizationFilters.Heading")}
                    description={t("pa.organizationFilters.Subtext")}
                    filters={orgFiltersData}
                    selectedFilterValues={
                      selectedFilters[FilterCategoryName?.organization]
                    }
                    onFilterSelected={(_selectedFilters) => {
                      handleOrgTabSelectedFilters(_selectedFilters);
                    }}
                  />
                ) : (
                  !isLoading() &&
                  (typeof filters?.organization === DataTypes?.STRING ? (
                    <div className="no-data"> {filters?.organization}</div>
                  ) : (
                    <div className={styles.noOrgData}>
                      {filters.error?.organization?.isEmpty && (
                        <>
                          <div className={styles.tryAgainText}>
                            {t("pa.noFiltersOptionFound")}
                          </div>
                          <div>
                            <Button
                              type="button"
                              onClick={() => {
                                let localfilters = {
                                  ...selectedFilterToBePersisted.organization
                                };
                                if (activeFIlterName === "JOBFAMILY") {
                                  localfilters[activeFIlterName] = "";
                                  localfilters["JOBSUBFAMILY"] = "";
                                } else {
                                  localfilters[activeFIlterName] = "";
                                }
                                setIsDataEmpty(true);
                                setSelectedFilters({
                                  ...selectedFilters,
                                  organizationFilters: localfilters
                                });
                                handleTabChange(activeTab - 1, "Back");
                              }}
                            >
                              {t("pa.backtoFiltersScreen")}
                            </Button>
                          </div>
                        </>
                      )}
                      {filters.error?.organization?.isFailed && (
                        <>
                          <div className={styles.tryAgainText}>
                            <b>{t("pa.hmmTryAagin")}</b>
                          </div>
                          <p>{t("pa.tryReloading")}</p>
                        </>
                      )}
                    </div>
                  ))
                )}
              </TabPanelContent> */}
              <TabPanelContent index={2} value={activeTab}>
                <ReportFilters
                  name={FilterCategoryName?.downloadYourReport}
                  title={t("pa.exportYourReport.Heading")}
                  description={t("pa.exportYourReport.Subtext")}
                  filters={exportFiltersState}
                  selectedFilterValues={
                    selectedFilters[FilterCategoryName?.downloadYourReport]
                  }
                  onFilterSelected={(_selectedFilters) => {
                    handleExportTabSelectedFilters(_selectedFilters);
                  }}
                />
              </TabPanelContent>
              {isLoading() && <Loader />}
            </div>
            <div className={styles.overviewSection}>
              <ReportOverview />
            </div>
          </div>
        </div>
      </div>
      <div className={styles.pageFooter}>
        <div className={styles.footerContainer}>
          <div className={styles.left}>
            <CustomButtonComponent
              onClick={() => {
                history(PAGE_URL.HOME);
              }}
              text={t("pa.cancel")}
              underLine={true}
            />
          </div>
          <div className={styles.right}>
            <CustomButtonComponent
              variant={"outlined"}
              onClick={() => {
                activeTab === 0
                  ? history(PAGE_URL.HOME)
                  : handleTabChange(activeTab - 1, "Back");
              }}
              icon={<KeyboardArrowLeftIcon />}
              text={t("pa.back")}
              iconPlacement="left"
            />
            {activeTab !== 2 && (
              <CustomButtonComponent
                variant="outlined"
                onClick={() => {
                  handleTabChange(activeTab + 1, "Next");
                }}
                icon={<KeyboardArrowRightIcon />}
                text={t("pa.next")}
                iconPlacement="right"
              />
            )}
          </div>
        </div>
      </div>
      {showExportDialog && (
        <ReportExportDialog
          onClose={() => {
            setShowExportDialog(false);
          }}
          onExport={() => {
            downloadReport();
          }}
        />
      )}
    </div>
  );
};

export default RewardBenchMark;
